import { injectable } from 'inversify';

import { FileInputValue } from '@vk-hr-tek/ui/input';

import {
  AttributeEmployee,
  AttributeValueGoal,
  AttributeValueGoalChange,
  AttributeValueGoalReview,
  AttributeValueGoalsFinalReview,
} from '@app/gen/events';

import { FormAttributesRequest } from '../types';

type Document = Record<string, string | FileInputValue | FileInputValue[]>;

@injectable()
export class EventsAttributesMapper {
  formatAttributes(
    submittedValues: Record<
      string,
      string | FileInputValue | FileInputValue[] | Document[]
    >,
  ) {
    const formattedValues: FormAttributesRequest = {};
    const values: Record<
      string,
      | string
      | FileInputValue
      | FileInputValue[]
      | Document[]
      | AttributeValueGoalReview
      | AttributeValueGoalsFinalReview
      | AttributeValueGoalsFinalReview[]
      | AttributeValueGoalChange
      | AttributeValueGoalChange[]
      | AttributeValueGoal
      | AttributeValueGoal[]
      | AttributeEmployee
      | AttributeEmployee[]
    > = {};

    for (const key in submittedValues) {
      if (key.startsWith('_dateTime')) {
        continue;
      }

      if (
        key.startsWith('_attribute_mandatory_') ||
        key.startsWith('_attribute_optional_')
      ) {
        const correctKey = key
          .replace('_mandatory', '')
          .replace('_optional', '');

        values[correctKey] = [
          ...((values[correctKey] as FileInputValue[]) || []),
          ...(submittedValues[key] as FileInputValue[]),
        ];
      } else {
        values[key] = submittedValues[key];
      }
    }

    for (const key in values) {
      if (key.startsWith('document_')) {
        if (Array.isArray(values[key])) {
          const documents = values[key] as Document[];

          const attributeIds = new Set<string>();

          documents.forEach((document) => {
            Object.keys(document).forEach((attributeId) => {
              if (attributeId === 'sub_block') {
                const subBlocks = document.sub_block;

                if (Array.isArray(subBlocks)) {
                  subBlocks.forEach((subBlock) => {
                    Object.keys(subBlock).forEach((subBlockId) => {
                      attributeIds.add(subBlockId);
                    });
                  });
                }
              } else {
                attributeIds.add(attributeId);
              }
            });
          });

          attributeIds.forEach((attribute) => {
            formattedValues[attribute] = documents.map((document) => {
              const docSubBlock = document.sub_block;

              if (docSubBlock && Array.isArray(docSubBlock)) {
                const docSubBlockValues: string[] = [];

                docSubBlock.forEach((subBlock) => {
                  Object.entries(subBlock).forEach(([fieldId, fieldValue]) => {
                    if (fieldId === attribute) {
                      docSubBlockValues.push(fieldValue);
                    }
                  });
                });
                if (docSubBlockValues.length > 0) {
                  return docSubBlockValues;
                }
              }
              if (
                document[attribute] &&
                typeof document[attribute] !== 'string'
              ) {
                if (Array.isArray(document[attribute])) {
                  const mappedValues = (
                    document[attribute] as (FileInputValue | string)[]
                  ).map((value: FileInputValue | string) => {
                    if (typeof value === 'string') {
                      return value;
                    } else {
                      return value?.value;
                    }
                  });

                  return mappedValues.length ? mappedValues : '';
                } else {
                  return (document[attribute] as FileInputValue).value;
                }
              } else if (
                document[attribute] &&
                (typeof document[attribute] === 'string' ||
                  typeof document[attribute] === 'number')
              ) {
                return document[attribute].toString();
              } else {
                return '';
              }
            });
          });
        }
      } else if (values[key] && typeof values[key] !== 'string') {
        if (Array.isArray(values[key])) {
          formattedValues[key] = (
            values[key] as
              | FileInputValue[]
              | AttributeValueGoalsFinalReview[]
              | AttributeValueGoalChange[]
              | AttributeValueGoal[]
          ).map((value) => {
            if (typeof value !== 'object') {
              return value;
            }

            if (
              'goal' in value ||
              'goals' in value ||
              'share' in value ||
              'new_share' in value
            ) {
              return value;
            }

            return (value as FileInputValue)?.value;
          });
        } else if (
          typeof values[key] === 'object' &&
          values[key] !== null &&
          ('goals' in values[key] || 'percentage_manager' in values[key])
        ) {
          const item = values[key];
          formattedValues[key] = item;
        } else {
          formattedValues[key] = (values[key] as FileInputValue).value;
        }
      } else if (
        values[key] &&
        (typeof values[key] === 'string' || typeof values[key] === 'number')
      ) {
        formattedValues[key] = values[key].toString();
      }
    }
    return formattedValues;
  }
}
